import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  partnersService,
  Partner,
  PartnerVoucher,
  PartnerLocation,
} from "../../services/partners";
import {
  VOUCHER_STATUS_OPTIONS,
  voucherStatusLabel,
  voucherStatusBadgeClass,
  formatDateTime,
} from "../vouchers/voucherDisplay";
import { useToast } from "../../shared/hooks/useToast";
import Toast from "../../shared/components/Toast";
import ImportFeedback from "./ImportFeedback";
import { runExcelImport } from "./runExcelImport";
import { redeemIssuedVoucher } from "../vouchers/redeemVoucher";

interface PartnerDetailPageProps {
  onPageChange: (page: string) => void;
}

const PartnerDetailPage: React.FC<PartnerDetailPageProps> = ({
  onPageChange,
}) => {
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const locationFileInputRef = useRef<HTMLInputElement>(null);
  const { toast, showToast } = useToast();

  const [partner, setPartner] = useState<Partner | null>(null);
  const [activeTab, setActiveTab] = useState<"vouchers" | "locations">(
    "vouchers"
  );
  const [vouchers, setVouchers] = useState<PartnerVoucher[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState(false);
  const [error, setError] = useState("");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [importErrors, setImportErrors] = useState<
    Array<{ row: number; message: string }>
  >([]);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);

  const [locations, setLocations] = useState<PartnerLocation[]>([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [debouncedLocationSearch, setDebouncedLocationSearch] = useState("");
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationImportFile, setLocationImportFile] = useState<File | null>(
    null
  );
  const [isImportingLocations, setIsImportingLocations] = useState(false);
  const [locationImportMessage, setLocationImportMessage] = useState("");
  const [locationImportErrors, setLocationImportErrors] = useState<
    Array<{ row: number; message: string }>
  >([]);
  const [deletingLocationId, setDeletingLocationId] = useState<string | null>(
    null
  );

  const fetchVouchers = useCallback(
    async (partnerId: string, status: string) => {
      try {
        setIsLoadingVouchers(true);
        const response = await partnersService.getPartnerVouchers(
          partnerId,
          status === "all" ? undefined : status
        );
        setVouchers(response.data || []);
      } catch (err: any) {
        showToast(err.message || "Không thể tải danh sách voucher", "error");
      } finally {
        setIsLoadingVouchers(false);
      }
    },
    []
  );

  const fetchLocations = useCallback(
    async (partnerId: string, search: string) => {
      try {
        setIsLoadingLocations(true);
        const response = await partnersService.getPartnerLocations(partnerId, {
          search: search || undefined,
        });
        setLocations(response.data || []);
      } catch (err: any) {
        showToast(
          err.message || "Không thể tải danh sách cửa hàng",
          "error"
        );
      } finally {
        setIsLoadingLocations(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!id) {
      setError("Không tìm thấy ID đối tác");
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadPartner = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await partnersService.getPartner(id);
        if (!cancelled) {
          setPartner(response.data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || "Không thể tải thông tin đối tác");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPartner();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedLocationSearch(locationSearch.trim()),
      300
    );
    return () => clearTimeout(timer);
  }, [locationSearch]);

  useEffect(() => {
    if (!id || !partner) return;
    fetchVouchers(id, statusFilter);
  }, [id, partner, statusFilter, fetchVouchers]);

  useEffect(() => {
    if (!id || !partner) return;
    fetchLocations(id, debouncedLocationSearch);
  }, [id, partner, debouncedLocationSearch, fetchLocations]);

  const handleImport = async () => {
    await runExcelImport({
      partnerId: id,
      file: importFile,
      emptyFileMessage: "Vui lòng chọn file Excel để import",
      itemLabel: "voucher",
      importFn: partnersService.importPartnerVouchers.bind(partnersService),
      setBusy: setIsImporting,
      setMessage: setImportMessage,
      setErrors: setImportErrors,
      clearFile: () => {
        setImportFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
      showToast,
      onSuccess: () => fetchVouchers(id!, statusFilter),
    });
  };

  const handleRedeem = async (voucher: PartnerVoucher) => {
    if (!id) return;
    await redeemIssuedVoucher({
      partnerId: id,
      voucher,
      setRedeemingId,
      showToast,
      onUpdated: (updated) => {
        setVouchers((prev) =>
          prev.map((v) => (v._id === voucher._id ? updated : v))
        );
      },
    });
  };

  const handleLocationImport = async () => {
    await runExcelImport({
      partnerId: id,
      file: locationImportFile,
      emptyFileMessage: "Vui lòng chọn file Excel để import cửa hàng",
      itemLabel: "cửa hàng",
      importFn: partnersService.importPartnerLocations.bind(partnersService),
      setBusy: setIsImportingLocations,
      setMessage: setLocationImportMessage,
      setErrors: setLocationImportErrors,
      clearFile: () => {
        setLocationImportFile(null);
        if (locationFileInputRef.current) {
          locationFileInputRef.current.value = "";
        }
      },
      showToast,
      onSuccess: () => fetchLocations(id!, debouncedLocationSearch),
    });
  };

  const handleDeleteLocation = async (location: PartnerLocation) => {
    if (!id) return;
    const confirmed = window.confirm(
      `Xóa cửa hàng "${location.name}"?\n${location.address}`
    );
    if (!confirmed) return;

    try {
      setDeletingLocationId(location._id);
      await partnersService.deletePartnerLocation(id, location._id);
      setLocations((prev) => prev.filter((l) => l._id !== location._id));
      showToast(`Đã xóa cửa hàng ${location.name}`);
    } catch (err: any) {
      showToast(err.message || "Không thể xóa cửa hàng", "error");
    } finally {
      setDeletingLocationId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">
          Đang tải thông tin đối tác...
        </span>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div>
        <button
          onClick={() => onPageChange("partners")}
          className="text-blue-600 dark:text-blue-400 hover:underline mb-4 text-sm"
        >
          ← Quay lại danh sách đối tác
        </button>
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error || "Không tìm thấy đối tác"}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => onPageChange("partners")}
          className="text-blue-600 dark:text-blue-400 hover:underline mb-3 text-sm"
        >
          ← Quay lại danh sách đối tác
        </button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {partner.name}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Quản lý voucher và cửa hàng của đối tác
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              partner.status === "active"
                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
            }`}
          >
            {partner.status === "active" ? "Hoạt động" : "Ngưng hoạt động"}
          </span>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-6">
          <div className="min-w-0 lg:col-span-3">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Quy tắc đổi quà
            </div>
            <p className="text-sm text-gray-800 dark:text-gray-200">
              {partner.fulfillment?.require_user_address
                ? "Giao hàng — yêu cầu địa chỉ người nhận"
                : partner.fulfillment?.require_location_select
                  ? "Chọn cửa hàng theo tỉnh/quận"
                  : "Mặc định — đổi quà tất cả chi nhánh toàn quốc"}
            </p>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Slug
            </div>
            {partner.slug ? (
              <div className="flex items-center gap-2 min-w-0">
                <code className="text-xs font-mono text-gray-800 dark:text-gray-200 truncate">
                  {partner.slug}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(partner.slug || "");
                    showToast("Đã sao chép slug");
                  }}
                  className="shrink-0 px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Sao chép
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Chưa có slug
              </p>
            )}
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Link chiến dịch
            </div>
            {partner.campaign_link ? (
              <div className="flex items-center gap-2 min-w-0">
                <a
                  href={partner.campaign_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate"
                  title={partner.campaign_link}
                >
                  {partner.campaign_link}
                </a>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(partner.campaign_link || "");
                    showToast("Đã sao chép link chiến dịch");
                  }}
                  className="shrink-0 px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Sao chép
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Không có chiến dịch
              </p>
            )}
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Token đối tác
            </div>
            {partner.partner_token ? (
              <div className="flex items-center gap-2 min-w-0">
                <code className="text-xs font-mono text-gray-800 dark:text-gray-200 truncate">
                  {partner.partner_token}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(partner.partner_token || "");
                    showToast("Đã sao chép token đối tác");
                  }}
                  className="shrink-0 px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Sao chép
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Chưa có token
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          <button
            type="button"
            onClick={() => setActiveTab("vouchers")}
            className={`pb-2.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "vouchers"
                ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            Voucher
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("locations")}
            className={`pb-2.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "locations"
                ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            Cửa hàng
          </button>
        </nav>
      </div>

      {activeTab === "vouchers" ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Danh sách voucher
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {VOUCHER_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  setImportFile(e.target.files?.[0] || null);
                  setImportMessage("");
                  setImportErrors([]);
                }}
                className="text-sm text-gray-700 dark:text-gray-300 max-w-[220px] file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300"
              />
              <button
                type="button"
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isImporting ? "Đang import..." : "Import"}
              </button>
            </div>
          </div>
          <ImportFeedback message={importMessage} errors={importErrors} />

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {isLoadingVouchers ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <span className="ml-3 text-gray-600 dark:text-gray-300">
                  Đang tải voucher...
                </span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Mã voucher
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Batch import
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Phát hành
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Đổi quà
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {vouchers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          Chưa có voucher nào
                          {statusFilter !== "all"
                            ? " với trạng thái đã chọn"
                            : ". Hãy import file Excel."}
                        </td>
                      </tr>
                    ) : (
                      vouchers.map((voucher) => (
                        <tr
                          key={voucher._id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                            {voucher.code}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${voucherStatusBadgeClass(
                                voucher.status
                              )}`}
                            >
                              {voucherStatusLabel(voucher.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {voucher.import_batch || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {formatDateTime(voucher.issued_at)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {formatDateTime(voucher.redeemed_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {voucher.status === "issued" ? (
                              <button
                                type="button"
                                onClick={() => handleRedeem(voucher)}
                                disabled={redeemingId === voucher._id}
                                className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                {redeemingId === voucher._id
                                  ? "Đang xử lý..."
                                  : "Đánh dấu đổi"}
                              </button>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Danh sách cửa hàng
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Import: name, address (bắt buộc); phone, province, district, ward
                (tùy chọn)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder="Tìm theo tên, địa chỉ, SĐT..."
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
              />
              <input
                ref={locationFileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  setLocationImportFile(e.target.files?.[0] || null);
                  setLocationImportMessage("");
                  setLocationImportErrors([]);
                }}
                className="text-sm text-gray-700 dark:text-gray-300 max-w-[220px] file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300"
              />
              <button
                type="button"
                onClick={handleLocationImport}
                disabled={!locationImportFile || isImportingLocations}
                className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isImportingLocations ? "Đang import..." : "Import"}
              </button>
            </div>
          </div>
          <ImportFeedback
            message={locationImportMessage}
            errors={locationImportErrors}
          />

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            {isLoadingLocations ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                <span className="ml-3 text-gray-600 dark:text-gray-300">
                  Đang tải cửa hàng...
                </span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Tên cửa hàng
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Địa chỉ
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        SĐT
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Khu vực
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Batch import
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {locations.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          {debouncedLocationSearch
                            ? "Không tìm thấy cửa hàng phù hợp"
                            : "Chưa có cửa hàng nào. Hãy import file Excel."}
                        </td>
                      </tr>
                    ) : (
                      locations.map((location) => {
                        const area = [
                          location.ward,
                          location.district,
                          location.province,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        return (
                          <tr
                            key={location._id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {location.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs">
                              {location.address}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {location.phone || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {area || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {location.import_batch || "—"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteLocation(location)}
                                disabled={deletingLocationId === location._id}
                                className="px-3 py-1.5 text-sm rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                              >
                                {deletingLocationId === location._id
                                  ? "Đang xóa..."
                                  : "Xóa"}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default PartnerDetailPage;
